import React, { useState } from "react";
import { CustomerUser, removeUserFromCustomer } from "@/api/users";
import { Link } from "react-router-dom";
import { DataGrid, DataGridColumn } from "@/ui-controls/DataGrid";
import MoreActionsButton from "@/ui-controls/MoreActionsButton";
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Persona,
  PersonaSize,
  PrimaryButton,
} from "@fluentui/react";

interface Props {
  CustomerId: number;
  UserList: CustomerUser[];
  onRemoveUsers?: (userIds: number[]) => any;
  onEditUser?: (user: CustomerUser) => any;
}

const CustomerUserDetailList = (props: Props) => {
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<CustomerUser | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const columns: DataGridColumn[] = [
    {
      key: "id",
      name: "Id",
      fieldName: "Id",
      minWidth: 50,
      maxWidth: 60,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "persona",
      name: "",
      fieldName: "",
      minWidth: 50,
      maxWidth: 70,
      isResizable: false,
      onRender: (item: CustomerUser) => {
        // Derive display name from the email (before the '@')
        const emailPrefix = item.EmailAddress?.split("@")[0] || "";
        return (
          <div
            style={{ display: "flex", alignItems: "center", height: "100%" }}
          >
            <Persona
              hidePersonaDetails
              text={emailPrefix}
              size={PersonaSize.size32}
            />
          </div>
        );
      },
    },
    {
      key: "emailAddress",
      name: "UserId NextGen Dispatch",
      fieldName: "EmailAddress",
      minWidth: 250,
      maxWidth: 450,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "tmsUsername",
      name: "Tms Username",
      fieldName: "TmsUsername",
      minWidth: 150,
      maxWidth: 250,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: CustomerUser) => {
        const value = item.TmsUsername || "";
        const needsEdit = !value;
        return (
          <div
            style={{
              padding: "4px 8px",
              border: needsEdit ? "1px solid red" : undefined,
              borderRadius: 2,
              height: "100%",
              display: "flex",
              alignItems: "center",
            }}
          >
            {value}
          </div>
        );
      },
    },
    {
      key: "Actions",
      name: "",
      minWidth: 50,
      maxWidth: 70,
      onRender: (item: CustomerUser) => (
        <MoreActionsButton
          menu={[
            {
              key: "edit",
              text: "Edit customer user info",
              iconProps: { iconName: "Edit" },
              onClick: () => {
                props.onEditUser?.(item);
              },
            },
            {
              key: "remove",
              text: "Remove user from customer",
              iconProps: { iconName: "Cancel" },
              onClick: () => {
                openRemoveDialog(item);
              },
            },
          ]}
        />
      ),
    },
  ];

  const openRemoveDialog = (user: CustomerUser) => {
    setSelectedUser(user);
    setIsRemoveDialogOpen(true);
  };

  const closeRemoveDialog = () => {
    if (isRemoving) {
      return;
    }

    setIsRemoveDialogOpen(false);
    setSelectedUser(null);
  };

  const confirmRemoveUser = async () => {
    if (!selectedUser) {
      return;
    }

    setIsRemoving(true);
    try {
      await removeUserFromCustomer(props.CustomerId, selectedUser.Id);
      if (props.onRemoveUsers) props.onRemoveUsers([selectedUser.Id]);
      closeRemoveDialog();
    } catch (error) {
      handleError(error as Error);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleError = (error: Error) => {
    // Error handling would go here in real app
  };

  return (
    <>
      <DataGrid items={[...props.UserList]} columns={columns} />
      <Dialog
        hidden={!isRemoveDialogOpen}
        onDismiss={closeRemoveDialog}
        dialogContentProps={{
          type: DialogType.normal,
          title: "Remove user from customer",
          subText: selectedUser
            ? `Remove ${selectedUser.EmailAddress} from this customer?`
            : "Remove this user from this customer?",
        }}
        modalProps={{
          isBlocking: true,
          isDarkOverlay: true,
        }}
      >
        <DialogFooter>
          <PrimaryButton
            onClick={confirmRemoveUser}
            disabled={isRemoving}
            text={isRemoving ? "Removing..." : "Remove"}
          />
          <DefaultButton
            onClick={closeRemoveDialog}
            text="Cancel"
            disabled={isRemoving}
          />
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default CustomerUserDetailList;
